import { useState } from 'react'

import {
	Box,
	Button,
	FormControlLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from '@mui/material'
import { GAME_TYPE, GameType } from 'constants/enums/GameTypeEnum'
import { brainstormText } from 'content/brainstormText'

import FacilitationHeader from 'components/FacilitationHeader'
import GeneratedExampleDropdown from 'components/GeneratedExampleDropdown'
import PromptBox from 'components/PromptBoxV2'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import { commonStyles } from 'styles/common.styles'

import { DisruptionType } from '../../types'
import { styles } from './PromptSection.styles'

export const TEST_ID = 'battle-ships'

export type Props = {
	currentResponses: string[][]
	disruptionType?: DisruptionType
	gameTypeEnum: GameType
	generateNewScenarioDisabled: () => boolean
	isLoading: boolean
	onDisruptionTypeChange?: (type: DisruptionType) => void
	prompt: string
	refreshPrompt: (overrideDisruptionType?: DisruptionType) => Promise<void>
	reloadIdeas: (newPrompt: string) => Promise<void>
}

const buildResponseItem = (solution: string[]) => {
	return (
		<Box sx={styles.exampleIdeasBox}>
			<ol data-testid="gen-ai-response-list">
				{solution.map((el: string, i: number) => (
					<Typography
						fontSize={16}
						component="li"
						key={`ai-solution-${i}`}
						variant="body1"
						style={styles.listItem}
					>
						{el}
					</Typography>
				))}
			</ol>
		</Box>
	)
}

const PromptSection = ({
	gameTypeEnum,
	prompt,
	isLoading,
	generateNewScenarioDisabled,
	currentResponses,
	refreshPrompt,
	reloadIdeas,
	onDisruptionTypeChange,
	disruptionType: externalDisruptionType,
}: Props) => {
	const [isEditMode, setIsEditMode] = useState(false)
	const [internalDisruptionType, setInternalDisruptionType] = useState(
		DisruptionType.Marketplace
	)
	const disruptionType = externalDisruptionType || internalDisruptionType
	const [showAIExampleIdeas, setShowAIExampleIdeas] = useState([false, false])

	const showSingleDropdown = (index: number) => {
		setShowAIExampleIdeas((prev) => {
			const newState = [...prev]
			newState[index] = !newState[index]
			return newState
		})
	}

	const handleSaveButtonClick = async () => {
		const textareaPrompt = document.getElementsByTagName('textarea')

		if (textareaPrompt && textareaPrompt.length > 0) {
			setIsEditMode(false)
			await reloadIdeas(textareaPrompt[0].value)
		}
	}

	const onClickCancelEdit = () => {
		setIsEditMode(false)
	}

	return (
		<>
			<Section2Title gameType="battleShips" />
			<UserEducationModal />
			<Box display={'flex'}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box sx={styles.container}>
					<Box sx={styles.innerColumn}>
						<Box
							display="flex"
							justifyContent="space-between"
							sx={styles.headerBox}
						>
							<Typography sx={commonStyles.header}>
								What would happen if your company suddenly
								experienced one of the following scenarios?
							</Typography>
						</Box>
						<FacilitationHeader
							onRefresh={refreshPrompt}
							onEdit={
								isEditMode
									? undefined
									: () => setIsEditMode(true)
							}
							isLoading={
								isLoading || generateNewScenarioDisabled()
							}
							refreshTestId={`${TEST_ID}-prompt-section__prompt-box_main-refresh`}
							editTestId={`${TEST_ID}-prompt-section-edit-prompt`}
						/>
						<Box display={'flex'} flexDirection={'row'}>
							<RadioGroup
								aria-label="disruption-type"
								name="disruption-type"
								value={disruptionType}
								onChange={(event) => {
									const newType = event.target
										.value as DisruptionType
									if (onDisruptionTypeChange) {
										// Let parent handle the change - no API call here
										// Parent decides whether to use cache or fetch
										onDisruptionTypeChange(newType)
									} else {
										setInternalDisruptionType(newType)
									}
								}}
								sx={styles.radioGroup}
								row
							>
								<FormControlLabel
									value="marketplace"
									control={<Radio sx={styles.radioButton} />}
									label={
										<Typography variant="subtitle2">
											MARKETPLACE DISRUPTION
										</Typography>
									}
									data-testid={`${TEST_ID}__marketplace-radio`}
								/>
								<Typography
									variant="body2"
									data-testid={`${TEST_ID}__support-text`}
									sx={{ width: '3.5rem' }}
								>
									{'OR'}
								</Typography>
								<FormControlLabel
									value="future"
									control={<Radio sx={styles.radioButton} />}
									label={
										<Typography variant="subtitle2">
											FUTURE TREND ADOPTION
										</Typography>
									}
									data-testid={`${TEST_ID}__future-radio`}
								/>
							</RadioGroup>
						</Box>
						{isEditMode ? (
							<>
								<TextField
									data-testid={`${TEST_ID}-prompt-section__edit-company-disruption`}
									defaultValue={prompt}
									multiline
									fullWidth
									inputRef={(input) => input && input.focus()}
								/>

								<Box sx={styles.editButtonsContainer}>
									<Button
										color="secondary"
										variant="contained"
										data-testid={`${TEST_ID}-prompt-section__edit-save-button`}
										onClick={async () =>
											await handleSaveButtonClick()
										}
									>
										<Typography>SAVE CHANGES</Typography>
									</Button>
									<Button
										variant="text"
										color="secondary"
										data-testid={`${TEST_ID}-prompt-section__edit-cancel-button`}
										onClick={onClickCancelEdit}
									>
										<Typography>CANCEL</Typography>
									</Button>
								</Box>
							</>
						) : (
							<>
								<PromptBox
									gameTypeEnum={GAME_TYPE.BATTLE_SHIPS}
									contentText={
										prompt || 'Generate Scenario Above'
									}
									data-testid="company-disruption"
									loading={isLoading}
									hidePromptContainerStyle={true}
								/>
							</>
						)}
					</Box>
					<Box>
						<Typography sx={styles.unsureText}>
							{brainstormText.global.ideasDropdown}
						</Typography>
						<Box sx={styles.dropdownContainer}>
							<Box
								key={`${TEST_ID}__generated-example_${0}`}
								data-testid={`${TEST_ID}__dropdown-box_${0}`}
								sx={styles.columnBox}
							>
								<GeneratedExampleDropdown
									isLoading={
										!currentResponses[0] ||
										currentResponses[0].length === 0
									}
									gameTypeEnum={gameTypeEnum}
									headerText={''}
									currentResponses={currentResponses[0]}
									customResponse={buildResponseItem(
										currentResponses[0]
									)}
									showAIExampleIdea={showAIExampleIdeas[0]}
									setShowAIExampleIdea={() =>
										showSingleDropdown(0)
									}
								/>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</>
	)
}

export default PromptSection
